import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  ContractModel,
  Currency,
  currencyMap, DictionaryEntryModel,
  DictionaryKey,
  extractNumber,
  FileModel,
  Folder,
  HxCurrencyPipe,
  HxStorageInvoiceService,
  HxStoreService,
  HxUiLabelPipe,
  InvoiceContractProperty,
  isoDate,
  StorageInvoiceModel,
  StorageInvoiceRequest,
  StoreBasicModel,
  StoreProductModel
} from 'hx-services';
import {
  HxDateSelectComponent,
  HxDictionarySelectComponent,
  HxErrorHandlerService,
  HxFileListComponent,
  HxFileUploadComponent,
  HxStoreSelectComponent,
  HxToastrService
} from 'hx-component';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { SuppliersSearchComponent } from '../../components/suppliers-search/suppliers-search.component';
import { ContractSearchComponent } from '../contract/search/contract-search.component';
import { SpendCategorySearchComponent } from '../spend/spend-category-search/spend-category-search.component';
import { StoreProductSelectComponent } from '../../components/store-product-select/store-product-select.component';

@Component({
    selector: 'app-storage-invoice-edit',
    templateUrl: './storage-invoice-edit.component.html',
    styleUrls: ['./storage-invoice-edit.component.css'],
    encapsulation: ViewEncapsulation.None,
    imports: [NgClass, RouterLink, NgIf, FormsModule, ReactiveFormsModule, HxStoreSelectComponent, HxDateSelectComponent, SuppliersSearchComponent, ContractSearchComponent, NgFor, SpendCategorySearchComponent, HxFileListComponent, HxFileUploadComponent, StoreProductSelectComponent, HxDictionarySelectComponent, NgbTooltip, TranslocoPipe, HxCurrencyPipe, HxUiLabelPipe]
})
export class StorageInvoiceEditComponent implements OnInit {
  @ViewChild('templateRef') public templateRef: TemplateRef<any>;
  invoiceFormKeys = {
    incomingInvoiceNumber: 'incomingInvoiceNumber',
    incomingInvoiceDate: 'incomingInvoiceDate',
    paymentType: 'paymentType',
    supplierId: 'supplierId',
    products: 'products',
    contractId: 'contractId',
    storeId: 'storeId',
    spendCategoryCode: 'spendCategoryCode',
  };
  productFormKeys = {
    storeProductId: 'storeProductId',
    amount: 'amount',
    totalValue: 'totalValue',
    issueDate: 'issueDate',
    expiryDate: 'expiryDate',
    spendType: 'spendType',
  };
  isLoading = {
    invoice: false,
    submit: false,
  };
  invoice?: StorageInvoiceModel;
  invoiceForm!: UntypedFormGroup;
  storeProducts: StoreProductModel[] = [];
  fileList: FileModel[] = [];
  deletedFileList = [];
  folderPurchase = Folder.PURCHASE;
  invContractProperty?: InvoiceContractProperty;
  selectedContract?: ContractModel;
  currency?: Currency;
  currencyName?: string;
  baseLink = `/api/vn/files/download/`;
  image = {
    name: null,
    path: null,
    mimeType: null
  };
  isStorageInvoiceFileRequired = false;
  organziationId: number;
  dictSpendTypeKey = DictionaryKey.spendType;

  constructor(
    private aRoute: ActivatedRoute,
    private storageInvoiceService: HxStorageInvoiceService,
    private router: Router,
    private toastr: HxToastrService,
    private fb: UntypedFormBuilder,
    private errorService: HxErrorHandlerService,
    private storeService: HxStoreService,
    private domSanitizer: DomSanitizer,
    public modal: NgbModal,
    private tr: TranslocoService
  ) {
  }

  ngOnInit() {
    this.createForm();

    this.aRoute.queryParamMap.subscribe(paramMap => {
      const id = extractNumber('id', paramMap);
      if (id) {
        this.loadData(id);
      }
    });
  }

  setFormValue(key, value) {
    this.invoiceForm.controls[key].patchValue(value);
  }

  async onSubmitForm() {
    this.isLoading.submit = true;
    const obj: StorageInvoiceRequest = this.invoiceForm.getRawValue();
    obj.fileIds = this.fileList.map(r => r.id);
    obj.id = this.invoice?.id;
    try {
      await this.storageInvoiceService.saveInvoice(obj);
      this.toastr.success(this.tr.translate('storage-invoice-edit.ts.saveSuccess'));
      this.router.navigate(['../'], {relativeTo: this.aRoute});
    } finally {
      this.isLoading.submit = false;
    }
  }

  addItem() {
    const items = this.asFormArray(this.invoiceFormKeys.products);
    items.push(this.createItem());
  }

  removeItem(index: number) {
    const items = this.asFormArray(this.invoiceFormKeys.products);
    items.removeAt(index);
    this.storeProducts.splice(index, 1);
  }

  asFormArray(path: string): UntypedFormArray {
    return this.invoiceForm.get(path) as UntypedFormArray;
  }

  getPricePerUnit(index: number): number {
    const productForm = this.asFormArray(this.invoiceFormKeys.products).controls[index] as UntypedFormArray;
    const amount = Number(productForm.get(this.productFormKeys.amount).value);
    const totalValue = Number(productForm.get(this.productFormKeys.totalValue).value);
    if (amount && totalValue) {
      return Number((totalValue / amount).toFixed(2));
    } else {
      return 0;
    }
  }

  onStoreProductSelected(product: StoreProductModel | StoreProductModel[] | undefined, index: number) {
    if (product && !Array.isArray(product)) {
      this.storeProducts[index] = product;
    } else {
      this.storeProducts.splice(index, 1);
    }
  }

  deleteFile(file, index) {
    this.fileList.splice(index, 1);
    this.deletedFileList.push(file);
  }

  updateDeletedList(event) {
    this.deleteFile(event.file, event.index);
  }

  updateFiles(event: FileModel) {
    this.fileList.push(event);
  }

  validateItemFn: (params: { productInfoId: number }) => boolean = params => {
    const obj: StorageInvoiceRequest = this.invoiceForm.getRawValue();
    return obj.products.map(r => r.productInfoId).includes(params.productInfoId);
  };

  onContractChanged(contract: ContractModel) {
    this.selectedContract = contract;
    this.currency = contract.currency as Currency;
    this.currencyName = currencyMap.get(this.currency)?.full;
  }

  showImage(file: FileModel): void {
    this.image.name = file.fileName;
    this.image.path = this.baseLink + file.guid;
    this.image.mimeType = file.mimeType;
    if (file.mimeType === 'application/pdf') {
      this.image.path = this.domSanitizer.bypassSecurityTrustResourceUrl(this.image.path);
    }

    this.modal.open(this.templateRef, {size: 'lg'}).result
      .then(() => this.resetImage())
      .catch(() => this.resetImage());
  }

  loadProperty(store?: StoreBasicModel | StoreBasicModel[]) {
    if (store && !Array.isArray(store)) {
      if (this.organziationId !== store.organizationId) {
        this.organziationId = store.organizationId;
        this.setFormValue('supplierId', null);
        this.setFormValue('contractId', null);
        this.asFormArray(this.invoiceFormKeys.products).clear();
        this.storeProducts = [];
      }
      this.storeService.getPublicPropertyByKey(store?.id, 'invoiceContract').subscribe(result => {
        if (result && result.value) {
          this.invContractProperty = result.value as InvoiceContractProperty;
          if (!this.invContractProperty.required) {
            this.currency = store?.currency;
            this.currencyName = currencyMap.get(this.currency)?.full;
          }
        } else {
          this.currency = store.currency;
          this.currencyName = currencyMap.get(this.currency)?.full;
        }
      });

      this.storeService.getPublicPropertyByKey(store?.id, 'storage-invoice-file').subscribe(result => {
        if (result && result.enabled) {
          this.isStorageInvoiceFileRequired = (result.value as { required: boolean }).required;
        } else {
          this.isStorageInvoiceFileRequired = false;
        }
      });
    }
  }

  selectSpendCategory(category?: DictionaryEntryModel) {
    if (category) {
      this.invoiceForm.controls.spendCategoryCode.patchValue(category.code);
    }
  }

  private createForm(): void {
    this.invoiceForm = this.fb.group({
      incomingInvoiceNumber: [''],
      incomingInvoiceDate: [isoDate(Date.now())],
      supplierId: [''],
      storeId: [null, [Validators.required]],
      products: this.fb.array([]),
      contractId: [''],
      spendCategoryCode: ['raw_materials'],
    });
  }

  private createItem() {
    return this.fb.group({
      storeProductId: [null],
      amount: [null],
      totalValue: [null],
      issueDate: [null],
      expiryDate: [null],
      spendType: [null],
    });
  }

  private loadData(id: number) {
    this.invoice = undefined;
    this.isLoading.invoice = true;
    this.storageInvoiceService.getStorageInvoiceById(id).subscribe(result => {
      this.invoice = result;
      this.isLoading.invoice = false;
    }, err => {
      this.isLoading.invoice = false;
      this.errorService.check(err.error);
    });
  }

  private resetImage() {
    this.image.name = null;
    this.image.path = '';
  }
}
